import { useEffect } from 'react';
import { BaseRow, ClientImg, LocaleSwitcher } from '@components';
import { ROLES } from '@constants/roles.ts';
import { USER_TYPES } from '@constants/userTypes.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useAdminScopeCleaner } from '@hooks/useAdminEditScopeCleaner.ts';
import { useCustomNavigate } from '@hooks/useCustomNavigate.ts';
import { useEffectOnce } from '@hooks/useEffectOnce.ts';
import { useInterval } from '@hooks/useInterval.ts';
import { usePointsNotifications } from '@hooks/usePointsNotifications.ts';
import { useRenderCustomBanner } from '@hooks/useRenderCustomBanner.tsx';
import { useUserGamificationHub } from '@hooks/useUserGamificationHub.ts';
import { useUserRoles } from '@hooks/useUserRoles.ts';
import { getCrewUserRankName, getShoreUserTitleName } from '@layouts/main/header/helpers.ts';
import { useLingui } from '@lingui/react/macro';
import { ROUTES } from '@router/constants.ts';
import { getFullUserInfo } from '@routes/___shared___/user.slice.ts';
import ProfileBar from '@routes/profile-bar/page.tsx';
import { getAdminCenterValidation } from '@routes/sidebar/admin-center/___shared___/model/adminCenter.slice.ts';
import { getClientDetails } from '@shared/model/clientDetails.slice.ts';

import { BaseHeaderLayoutWrapper, LeftSection, LogoWrapper } from './styled.ts';

export const MainHeader = () => {
    const { t } = useLingui();
    const userRoles = useUserRoles();
    const navigate = useCustomNavigate();

    const userFullInfo = useAppSelector((state) => state.user.userInfo);
    const score = useAppSelector((state) => state.user.score);
    const seenNotifications = useAppSelector((state) => state.notifications.seenNotifications);
    const isCrewUser = userFullInfo?.userType === USER_TYPES.Crew;
    const role = isCrewUser ? getCrewUserRankName(userFullInfo) : getShoreUserTitleName(userFullInfo);

    const isUser = userRoles.includes(ROLES.USER);

    const { firstName, lastName, avatarURI } = userFullInfo?.userPersonalData ?? {};

    const dispatch = useAppDispatch();
    const customBanner = useRenderCustomBanner();

    const handleLogoClick = () => {
        navigate(ROUTES.SIDEBAR.LEARNING);
    };

    usePointsNotifications();
    useUserGamificationHub();

    useAdminScopeCleaner();

    // get fresh user data after app load/F5 and after notification state changes
    useEffect(() => {
        void dispatch(getFullUserInfo());
    }, [dispatch, seenNotifications]);

    // get fresh user data every 20 minutes while the app is open
    useInterval(
        () => {
            void dispatch(getFullUserInfo());
        },
        20 * 60 * 1000, // 20 minutes
    );

    // load “session-static” data once and keep them in redux for logged-in user
    useEffectOnce(() => {
        void dispatch(getClientDetails());
    }, true);

    // get validation details on mount and persist it
    useEffectOnce(() => {
        // skip for user
        if (isUser) return;

        void dispatch(getAdminCenterValidation());
    }, true);

    return (
        <>
            {customBanner}
            <BaseHeaderLayoutWrapper>
                <BaseRow justify="space-between" align="middle" style={{ width: '100%' }}>
                    <BaseRow align="middle" justify="start">
                        <LeftSection>
                            <LogoWrapper onClick={handleLogoClick}>
                                <ClientImg />
                            </LogoWrapper>
                        </LeftSection>
                    </BaseRow>
                    <BaseRow align="middle" justify="end">
                        <LocaleSwitcher />
                        <ProfileBar
                            name={firstName || lastName ? `${firstName} ${lastName}` : t`Name`}
                            role={role}
                            notificationCount={userFullInfo?.userState?.totalNewNotifications ?? 0}
                            avatarUrl={avatarURI || ''}
                            score={score}
                        />
                    </BaseRow>
                </BaseRow>
            </BaseHeaderLayoutWrapper>
        </>
    );
};
